import { useEffect, useMemo, useState } from 'react';

import { create, devtools, persist } from '@fhs/zustand';
import { preloadedOrder } from '@fhs-legacy/frontend/src/state/order/preloaded-order';
import { frontendToBackendCartEntryMap } from '@fhs-legacy/frontend/src/utils/cart';
import LocalStorage, { StorageKeys } from '@fhs-legacy/frontend/src/utils/local-storage';
import type { ICartEntry } from '@rbi-ctg/menu';

import type { ICartStore, ICartStoreActions } from '../../types';

const initialState: ICartStore = {
  cartEntries: [],
  getBackendCartEntries: () => [],
  lastPriceCheck: Date.now(),
};

export const useCartStore = create<ICartStore & ICartStoreActions>()(
  // TODO: separate cart and order contexts into slices
  devtools(
    persist(
      (set, get) => {
        return {
          ...initialState,
          getBackendCartEntries: () => get().cartEntries.map(frontendToBackendCartEntryMap) ?? [],
          addItemToCartStore: (addedEntry: ICartEntry) =>
            set(state => ({
              ...state,
              cartEntries: [...state.cartEntries, addedEntry],
            })),
          removeItemFromCartStore: (entryToRemove: ICartEntry) => {
            set(state => ({
              ...state,
              cartEntries: state.cartEntries.filter(
                currentEntry => entryToRemove.cartId !== currentEntry.cartId
              ),
            }));
          },
          replaceItemsInCartStore: (newCartEntries: ICartEntry[]) => {
            set(state => ({
              ...state,
              cartEntries: newCartEntries,
            }));
          },
          emptyCartStore: () => set(initialState),
          updatePriceTimeStamp: () => set({ lastPriceCheck: Date.now() }),
        };
      },
      {
        name: StorageKeys.ORDER,
        skipHydration: true,
        storage: {
          getItem: () => {
            const order = preloadedOrder();
            if (!order) {
              return null;
            }

            return {
              state: {
                cartEntries: order.cartEntries ?? [],
              },
            };
          },
          setItem: (_, value) => {
            const currentOrder = LocalStorage.getItem(StorageKeys.ORDER) || {};

            const updatedOrder = {
              ...currentOrder,
              cartEntries: value.state.cartEntries ?? [],
              timestamp: Date.now(),
            };

            LocalStorage.setItem(StorageKeys.ORDER, updatedOrder);
          },
          removeItem: () => {
            const currentOrder = LocalStorage.getItem(StorageKeys.ORDER) || {};
            const updatedOrder = {
              ...currentOrder,
              cartEntries: [],
              timestamp: Date.now(),
            };

            LocalStorage.setItem(StorageKeys.ORDER, updatedOrder);
          },
        },
      }
    )
  )
);

/**
 * Makes the values of cart store read only/ accessible
 * It does not give access to the actions specifically related to the store
 * i.e. addItemToCartStore, removeItemFromCartStore;
 * Use useCartActions instead
 */
export const useCartValues = () => {
  const { cartEntries, lastPriceCheck, updatePriceTimeStamp } = useCartStore();
  const backendCartEntries = useMemo(
    () => (cartEntries ? cartEntries.map(frontendToBackendCartEntryMap) : []),
    [cartEntries]
  );
  return { cartEntries, backendCartEntries, lastPriceCheck, updatePriceTimeStamp };
};

export const useCartStoreHydration = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const unsubHydrate = useCartStore.persist.onHydrate(() => setHydrated(false));

    const unsubFinishHydration = useCartStore.persist.onFinishHydration(() => setHydrated(true));

    setHydrated(useCartStore.persist.hasHydrated());

    return () => {
      unsubHydrate();
      unsubFinishHydration();
    };
  }, []);

  return hydrated;
};
